import React, { cloneElement, isValidElement, PropsWithChildren, ReactElement, ReactNode } from 'react';
import classNames from 'classnames';
import styled from '@emotion/styled';
import { HelpTextProps } from '../text';
import { Stack } from '../stack';
import { FormLabel } from './FormLabel';
import { CSSValueWithLength } from '../../styles';
import { FormHelperText, FormHelperTextProps } from './FormHelperText';
import { InputSize, InputStatus } from '../input/types';
import { getInputHeightBySize } from '../input/styles';
import { Input, NumericInput } from '../input';
import { Dropdown, DropdownInput } from '../dropdown';
import { semantic_spacing } from '../../foundation';
import { text_style_specs } from '../../foundation/typography';

export type FormFieldPosition = 'left' | 'top';
export type FormFieldHelperTextProps = Omit<
  FormHelperTextProps & JSX.IntrinsicElements[Extract<FormHelperTextProps['as'], string>],
  'children' | 'status'
>;

export interface FormFieldProps {
  className?: string;
  /**
   * FormField의 상태를 지정하면 children과 helperText 상태를 공유합니다.
   * PDS의 Input, Dropdown, DropdownInput, NumericInput 컴포넌트만 공유하도록 제한합니다.
   * 다른 컴포넌트는 사용하더라도 helperText에만 status가 적용됩니다.
   */
  status?: InputStatus;
  label?: ReactNode;
  /**
   * 라벨 우측 렌더링될 Addon 요소
   */
  rightAddon?: ReactNode;
  required?: boolean;
  /**
   * HelpTextProps 타입으로 넘길 경우 라벨 우측 IconHelp14에 자동으로 popover 처리됩니다.
   */
  tooltip?: HelpTextProps;
  /**
   * FormHelperText 컴포넌트로 children 아래에 메세지를 표시합니다.
   */
  helperText?: ReactNode;
  helperTextProps?: FormFieldHelperTextProps;
  /**
   * 최소너비 80px이상인 경우 라벨 너비를 지정할 수 있습니다.
   *
   * @default 80
   */
  labelWidth?: CSSValueWithLength;
  /**
   * label 위치를 지정할 수 있습니다.
   *  - position 이 top 인 경우 좌측 정렬 고정입니다.
   *
   * @default 'left'
   */
  position?: FormFieldPosition;
  /**
   * 라벨 영역과 children wrapper의 최소 높이를 form control size에 맞춥니다.
   *
   * children의 size는 자동으로 변경하지 않습니다.
   *
   * @default 'medium'
   */
  size?: InputSize;
  children?: ReactNode;
  /** @default '100%' */
  width?: CSSValueWithLength;
}

const statusPropComponents = new Set<unknown>([Input, Dropdown, DropdownInput, NumericInput]);

const hasStatusPropsComponent = (children: ReactNode): children is ReactElement<{ status: InputStatus }> =>
  isValidElement(children) && statusPropComponents.has(children.type);

const getFormFieldLabelPaddingVerticalBySize = (size: InputSize) =>
  Math.max((getInputHeightBySize(size) - text_style_specs.Body_13.lineHeight) / 2, 0);

export const FormField = ({
  className,
  label,
  status,
  rightAddon,
  required,
  tooltip,
  helperText,
  helperTextProps,
  width = '100%',
  labelWidth = 80,
  position = 'left',
  size = 'medium',
  children,
}: PropsWithChildren<FormFieldProps>) => {
  const clonedChildren = status && hasStatusPropsComponent(children) ? cloneElement(children, { status }) : children;
  const labelHeight = getInputHeightBySize(size);
  const labelPaddingVertical = getFormFieldLabelPaddingVerticalBySize(size);

  return (
    <FormFieldWrapper className={classNames('pds-form-field', className)} position={position} width={width}>
      {label && (
        <FormFieldLabelContainer
          align='flex-start'
          labelHeight={labelHeight}
          labelPaddingVertical={labelPaddingVertical}
          position={position}
          width={position === 'left' ? labelWidth : undefined}
        >
          <FormLabel tooltip={tooltip} rightAddon={rightAddon} required={required} width='100%'>
            {label}
          </FormLabel>
        </FormFieldLabelContainer>
      )}
      <FormContent>
        <FormFieldChildrenWrapper data-testid='pds-form-field-children' position={position} size={size}>
          {clonedChildren}
        </FormFieldChildrenWrapper>
        {helperText && (
          <FormHelperText status={status} {...helperTextProps}>
            {helperText}
          </FormHelperText>
        )}
      </FormContent>
    </FormFieldWrapper>
  );
};

const FormFieldWrapper = ({
  position,
  className,
  width,
  children,
}: {
  position: FormFieldPosition;
  className?: string;
  width: CSSValueWithLength;
  children: ReactNode;
}) => {
  if (position === 'top') {
    return (
      <Stack direction='column' className={className} width={width} gap={8}>
        {children}
      </Stack>
    );
  }

  return (
    <Stack className={className} width={width} align='flex-start' gap={8}>
      {children}
    </Stack>
  );
};

const FormFieldLabelContainer = styled(Stack, {
  shouldForwardProp: (prop) => !['position', 'labelHeight', 'labelPaddingVertical'].includes(prop),
})<{ position: FormFieldPosition; labelHeight: number; labelPaddingVertical: number }>`
  ${({ position }) =>
    position === 'left' &&
    `
      min-width: 80px;
      flex-shrink: 0;
    `};
  ${({ position, labelHeight }) => position === 'left' && `min-height: ${labelHeight}px`};
  ${({ position, labelPaddingVertical }) => position === 'left' && `padding: ${labelPaddingVertical}px 0`};
`;

const FormContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${semantic_spacing.helper_text.gap.desktop};
  min-width: 0;
  width: 100%;
`;

const FormFieldChildrenWrapper = styled('div', {
  shouldForwardProp: (prop) => !['position', 'size'].includes(prop),
})<{ position: FormFieldPosition; size: InputSize }>`
  position: relative;
  ${({ position, size }) =>
    position === 'left' &&
    `
      display: flex;
      flex-direction: column;
      justify-content: center;
      gap: ${semantic_spacing.helper_text.gap.desktop};
      min-height: ${getInputHeightBySize(size)}px;
    `}

  > :where(.pds-copy-text, .pds-button, .pds-textbutton, .pds-checkbox, .pds-switch) {
    align-self: flex-start;
  }

  &:empty {
    display: none;
    min-height: 0;
  }
`;
